import { useMediaDevices } from '@livekit/components-react'
import { useTranslation } from 'react-i18next'

import type { DeviceKind } from './DeviceMenu'
import type { DeviceWarning } from './deviceWarning'
import { ReactComponent as MicIcon } from 'assets/icons/Microphone.svg'
import { ReactComponent as MicOffIcon } from 'assets/icons/MicrophoneOff.svg'
import { ReactComponent as CameraIcon } from 'assets/icons/Video.svg'
import { ReactComponent as CameraOffIcon } from 'assets/icons/VideoOff.svg'

import { DeviceMenu } from './DeviceMenu'
import styles from './prejoin.module.css'

export type DeviceButtonProps = {
  isOn: boolean
  label: string
  onToggle: () => void
  kind: DeviceKind
  deviceId: string
  warning?: DeviceWarning
  onDeviceChange: (deviceId: string) => void
}

export function DeviceButton({
  isOn,
  label,
  onToggle,
  kind,
  deviceId,
  warning,
  onDeviceChange
}: DeviceButtonProps) {
  const { t } = useTranslation()

  const devices = useMediaDevices({ kind })

  const [OnIcon, OffIcon] =
    kind === 'audioinput' ? [MicIcon, MicOffIcon] : [CameraIcon, CameraOffIcon]

  const warningMessage = warning && t(warning.messageKey)

  const buttonClassName = [
    styles.deviceButton,
    isOn ? styles.deviceButtonOn : '',
    warning ? styles.deviceButtonWarning : ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={styles.device}>
      <button
        type="button"
        className={buttonClassName}
        onClick={onToggle}
        disabled={!!warning?.isBlocking}
        aria-label={
          warningMessage
            ? t('chat.call.labelWithWarning', { label, warning: warningMessage })
            : label
        }
        aria-pressed={isOn}
        title={warningMessage ?? label}
      >
        {isOn ? <OnIcon aria-hidden /> : <OffIcon aria-hidden />}
        {!!warning && (
          <span className={styles.warningBadge} aria-hidden>
            !
          </span>
        )}
      </button>
      <div className={styles.deviceMenu}>
        <DeviceMenu
          kind={kind}
          devices={devices}
          activeDeviceId={deviceId}
          disabled={!!warning || !devices.length}
          iconSize={16}
          triggerClassName={styles.deviceTrigger}
          onSelect={onDeviceChange}
        />
      </div>
    </div>
  )
}
